import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShoppingBag } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

function ProductCard({ product, index }) {
  const cart = useCart();
  const navigate = useNavigate();
  const hasSizes = product.sizes?.length > 0;
  const variations = product.variations || [];
  const hasVariations = variations.length > 0;

  const priceLabel = () => {
    if (!hasVariations) return `$${Number(product.price).toFixed(0)}`;
    const prices = variations.map((v) => Number(v.price));
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    if (min === max) return min === 0 ? "Free" : `$${min.toFixed(0)}`;
    if (min === 0) return `Free – $${max.toFixed(0)}`;
    return `From $${min.toFixed(0)}`;
  };

  const addToCart = () => {
    cart.add({
      type: "product", ref_id: product.id, name: product.name,
      unit_price: Number(product.price), image: product.image, quantity: 1, size: null,
    });
    toast.success("Added to cart");
  };

  return (
    <motion.article
      data-testid={`product-card-${product.id}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, delay: index * 0.08, ease: EASE }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-gold/15 bg-ink-card/70 transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40 hover:shadow-[0_30px_60px_-20px_rgba(212,175,55,0.22)]"
    >
      <Link to={`/shop/${product.id}`} data-testid={`product-link-${product.id}`} className="relative block overflow-hidden bg-gradient-to-b from-ink-edge to-ink">
        <img
          src={product.image || "/assets/product-book.png"}
          alt={product.name}
          className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute right-4 top-4 rounded-full bg-ink/80 px-3.5 py-1.5 font-display text-base font-semibold text-gold backdrop-blur">
          {priceLabel()}
        </span>
      </Link>
      <div className="flex flex-1 flex-col p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold/70">{product.category}</span>
        <Link to={`/shop/${product.id}`} className="mt-2 transition-colors hover:text-gold">
          <h3 className="font-display text-lg font-semibold leading-snug">{product.name}</h3>
        </Link>
        <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{product.description}</p>
        {hasVariations && (
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            {variations.length} ticket options
          </p>
        )}
        {!hasVariations && hasSizes && (
          <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            Sizes {product.sizes.join(" · ")}
          </p>
        )}
        <Button
          data-testid={`product-buy-button-${product.id}`}
          onClick={() => (hasVariations || hasSizes ? navigate(`/shop/${product.id}`) : addToCart())}
          className="mt-5 w-full rounded-full bg-gold py-5 font-semibold text-ink transition-all duration-300 hover:bg-gold-hover"
        >
          <ShoppingBag className="mr-2 h-4 w-4" /> {hasVariations || hasSizes ? "Select Options" : "Add to Cart"}
        </Button>
      </div>
    </motion.article>
  );
}

export default function ShopSection() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setProducts(data))
      .catch(() => toast.error("Could not load the shop."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="shop" data-testid="shop-section" className="relative border-y border-gold/10 bg-ink-card/40 py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="mb-14 flex flex-wrap items-end justify-between gap-6"
        >
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Merchandise & Publications</span>
            <h2 className="mt-4 max-w-xl font-display text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
              Wear the mission. Read the roadmap.
            </h2>
          </div>
          <p className="max-w-sm text-sm text-muted-foreground">
            <span className="text-gold">Thrills & Chills of Leadership</span> — 8 chapters that shift perspectives, prepare aspiring leaders, and refine veterans.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20" data-testid="shop-loading"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>
        ) : products.length === 0 ? (
          <p className="py-16 text-center text-muted-foreground" data-testid="shop-empty">Products coming soon.</p>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
}
